import { getLeagueOfPredictorsAPIGateway } from '@/api/generated';
import type {
  EventPoolMode as ApiEventPoolMode,
  EventPoolStatus as ApiEventPoolStatus,
  EventStatus as ApiEventStatus,
  ProfileDetails,
  ProfileDetailsById,
  WalletDetails,
  WalletCurrency,
} from '@/api/generated';
import { EventPoolMode } from '@/types/eventPools/enums';
import { EventStatus } from '@/types/events/enums';
import { formatDate, formatTime } from '@/services/formatDateTime';
import type { Pagination } from '@/types/ui';
import type {
  TCompetition,
  TCompetitionsApiResponse,
} from '@/types/competitions';
import type { TCountry, TCountriesApiResponse } from '@/types/countries';
import type { TEvent, TEventApiResponse } from '@/types/events';
import type { TEventPools, TEventPoolsApiResponse } from '@/types/eventPools';
import type { TTeam, TTeamsApiResponse } from '@/types/teams';
import { UserStatus, type TUser } from '@/types/users';

const api = getLeagueOfPredictorsAPIGateway();

const EVENT_STATUS_TO_API: Record<number, ApiEventStatus> = {
  [EventStatus.Waiting]: 'waiting',
  [EventStatus.Started]: 'started',
  [EventStatus.Finished]: 'finished',
  [EventStatus.Cancelled]: 'cancelled',
};

const EVENT_STATUS_FROM_API: Record<ApiEventStatus, EventStatus> = {
  waiting: EventStatus.Waiting,
  started: EventStatus.Started,
  finished: EventStatus.Finished,
  cancelled: EventStatus.Cancelled,
};

const EVENT_POOL_MODE_TO_API: Record<number, ApiEventPoolMode> = {
  [EventPoolMode.SingleMode]: 'singleMode',
  [EventPoolMode.DuelMode]: 'duelMode',
  [EventPoolMode.LeagueMode]: 'leagueMode',
  [EventPoolMode.TournamentMode]: 'tournamentMode',
};

const EVENT_POOL_MODE_FROM_API: Record<ApiEventPoolMode, EventPoolMode> = {
  singleMode: EventPoolMode.SingleMode,
  duelMode: EventPoolMode.DuelMode,
  leagueMode: EventPoolMode.LeagueMode,
  tournamentMode: EventPoolMode.TournamentMode,
};

const EVENT_POOL_STATUS_TO_API: Record<number, ApiEventPoolStatus> = {
  101: 'opened',
  102: 'closed',
  200: 'filled',
};

const EVENT_POOL_STATUS_FROM_API: Record<ApiEventPoolStatus, number> = {
  opened: 101,
  closed: 102,
  filled: 200,
  calculated: 200,
};

const toApiEventStatus = (status: number): ApiEventStatus =>
  EVENT_STATUS_TO_API[status] ?? 'waiting';

const fromApiEventStatus = (status?: ApiEventStatus | null): EventStatus =>
  status ? EVENT_STATUS_FROM_API[status] : EventStatus.Waiting;

const toApiEventPoolMode = (mode: number): ApiEventPoolMode =>
  EVENT_POOL_MODE_TO_API[mode] ?? 'singleMode';

const fromApiEventPoolMode = (mode?: ApiEventPoolMode | null): EventPoolMode =>
  mode ? EVENT_POOL_MODE_FROM_API[mode] : EventPoolMode.SingleMode;

const toApiEventPoolStatus = (status: number): ApiEventPoolStatus =>
  EVENT_POOL_STATUS_TO_API[status] ?? 'opened';

const fromApiEventPoolStatus = (status?: ApiEventPoolStatus | null): number =>
  status ? EVENT_POOL_STATUS_FROM_API[status] : 101;

const mapCountry = (country: any): TCountry => ({
  countryId: country?.countryId ?? '',
  name: country?.name ?? '',
});

const mapCompetition = (competition: any): TCompetition => ({
  competitionId: competition?.competitionId ?? '',
  name: competition?.name ?? '',
  rang: competition?.rang ?? '',
  countryName: competition?.country?.name ?? competition?.countryName ?? '',
  countryId: competition?.countryId ?? competition?.country?.countryId ?? '',
  country: competition?.country ? mapCountry(competition.country) : undefined,
  isFromApi: competition?.isFromApi ?? false,
});

const mapTeam = (team: any): TTeam => ({
  teamId: team?.id ?? team?.teamId ?? '',
  name: team?.teamName ?? team?.name ?? '',
  resultScore: Number(team?.resultScore ?? 0),
  winProbability: Number(team?.winProbability ?? 0),
  emblemUrl: team?.emblem?.url ?? null,
  countryName: team?.country?.name ?? team?.countryName ?? '',
  countryId: team?.countryId ?? team?.country?.countryId,
  country: team?.country ? mapCountry(team.country) : undefined,
  isFromApi: team?.isFromApi ?? false,
});

const mapEventTeam = (team: any, resultScore = 0, winProbability = 0) => ({
  id: team?.id ? Number(team.id) : undefined,
  teamId: team?.id ?? '',
  teamName: team?.teamName ?? '',
  resultScore: Number(team?.resultScore ?? resultScore) ?? 0,
  winProbability: Number(team?.winProbability ?? winProbability) ?? 0,
  isFromApi: team?.isFromApi ?? false,
});

const getEventStart = (event: any) => {
  if (event?.startAtUtc) {
    const startAt = new Date(event.startAtUtc);

    if (!Number.isNaN(startAt.getTime())) {
      return {
        startDate: formatDate(startAt),
        startTime: formatTime(startAt),
      };
    }
  }

  return {
    startDate: event?.startDate ?? '',
    startTime: event?.startTime ?? '00:00:00',
  };
};

const dateTimeToUtcIso = (
  dateValue: string | Date,
  timeValue: string,
): string => {
  if (!dateValue || !timeValue) return new Date().toISOString();

  const [hours, minutes, seconds] = timeValue.split(':').map(Number);

  // Build a Date in the *browser's local timezone* so that toISOString()
  // correctly shifts the wall-clock time the user entered to UTC.
  if (dateValue instanceof Date) {
    if (Number.isNaN(dateValue.getTime())) return new Date().toISOString();

    const date = new Date(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate(),
      hours,
      minutes,
      seconds || 0,
    );

    return date.toISOString();
  }

  const [year, month, day] = dateValue.split('-').map(Number);
  const date = new Date(year, month - 1, day, hours, minutes, seconds || 0);

  return Number.isNaN(date.getTime())
    ? new Date().toISOString()
    : date.toISOString();
};

const mapEvent = (event: any): TEvent => {
  const { startDate, startTime } = getEventStart(event);
  const blueTeamResultScore = Number(event?.blueTeamResultScore ?? 0);
  const redTeamResultScore = Number(event?.redTeamResultScore ?? 0);
  const blueTeamWinProbability = Number(event?.blueTeamWinProbability ?? 0);
  const redTeamWinProbability = Number(event?.redTeamWinProbability ?? 0);

  return {
    id: event?.id ?? '',
    countryId: event?.country?.countryId ?? event?.countryId ?? '',
    competionId:
      event?.competition?.competitionId ?? event?.competitionId ?? '',
    country: event?.country ? mapCountry(event.country) : undefined,
    competition: event?.competition
      ? mapCompetition(event.competition)
      : undefined,
    startDate,
    startTime,
    status: fromApiEventStatus(event?.status as ApiEventStatus),
    blueTeamResultScore,
    redTeamResultScore,
    blueTeamWinProbability,
    redTeamWinProbability,
    isFromApi: event?.isFromApi ?? false,
    blueTeam: mapEventTeam(
      event?.blueTeam,
      blueTeamResultScore,
      blueTeamWinProbability,
    ),
    redTeam: mapEventTeam(
      event?.redTeam,
      redTeamResultScore,
      redTeamWinProbability,
    ),
  };
};

const mapEventPool = (pool: any): TEventPools => ({
  id: pool?.id,
  date: pool?.date,
  poolDate: pool?.date ?? '',
  mode: fromApiEventPoolMode(
    pool?.mode as ApiEventPoolMode,
  ) as unknown as EventStatus,
  status: fromApiEventPoolStatus(
    pool?.status as ApiEventPoolStatus,
  ) as unknown as EventStatus,
  events: (pool?.events ?? []).map(mapEvent),
});

export const getCountries = async (
  pagination: Pagination,
): Promise<TCountriesApiResponse> => {
  const response = await api.getApiV1CountryCollection({
    Skip: pagination.skip,
    Take: pagination.take,
  });

  const countries = (response?.countries ?? []).map(mapCountry);
  return {
    countries,
    count: (response as any)?.count ?? countries.length,
  };
};

export const getShortCountries = async (): Promise<TCountriesApiResponse> => {
  const response = await api.getApiV1CountryCollection();
  const countries = (response?.countries ?? []).map(mapCountry);
  return {
    countries,
    count: (response as any)?.count ?? countries.length,
  };
};

export const getCompetitions = async (
  pagination: Pagination,
): Promise<TCompetitionsApiResponse> => {
  const response = await api.getApiV1CompetitionCollection({
    Skip: pagination.skip,
    Take: pagination.take,
  });

  const competition = (response?.competition ?? []).map(mapCompetition);
  return {
    competition,
    count: (response as any)?.count ?? competition.length,
  };
};

export const getShortCompetitions =
  async (): Promise<TCompetitionsApiResponse> => {
    const response = await api.getApiV1CompetitionCollection();
    const competition = (response?.competition ?? []).map(mapCompetition);
    return {
      competition,
      count: (response as any)?.count ?? competition.length,
    };
  };

export const getCompetitionsByCountry = async (
  countryId: string,
): Promise<TCompetitionsApiResponse> => {
  const response = await api.getApiV1CompetitionCollectionCountryId(countryId);
  const raw = (response as any)?.competition;

  const competition = Array.isArray(raw)
    ? raw.map(mapCompetition)
    : raw
      ? [mapCompetition(raw)]
      : [];

  return {
    competition,
    count: (response as any)?.count ?? competition.length,
  };
};

export const getTeams = async (
  pagination: Pagination,
): Promise<TTeamsApiResponse> => {
  const response = await api.getApiV1TeamCollection({
    Skip: pagination.skip,
    Take: pagination.take,
  });

  const teams = (response?.teams ?? []).map(mapTeam);
  return {
    teams,
    count: response?.count ?? teams.length,
  };
};

export const searchTeams = async (teamName?: string): Promise<TTeam[]> => {
  const response = await api.getApiV1TeamCollection({
    TeamName: teamName || undefined,
    Skip: 0,
    Take: 20,
  });
  return (response?.teams ?? []).map(mapTeam);
};

export const getEvents = async (
  pagination: Pagination,
): Promise<TEventApiResponse> => {
  const response = await api.getApiV1EventCollection({
    Skip: pagination.skip,
    Take: pagination.take,
    ...(pagination.date ? { date: pagination.date } : {}),
  });

  return {
    events: (response?.events ?? []).map(mapEvent),
    count: response?.count ?? 0,
  };
};

export const getEventPools = async (
  pagination: Pagination,
  mode: number,
): Promise<TEventPoolsApiResponse> => {
  const response = await api.getApiV1EventPoolCollection({
    Skip: pagination.skip,
    Take: pagination.take,
    mode: toApiEventPoolMode(mode),
  });

  const eventPools = (response?.eventPools ?? []).map(mapEventPool);
  return {
    eventPools,
    count: response?.count ?? eventPools.length,
  };
};

export const getPool = async (poolId: string): Promise<TEventPools> => {
  const response = await api.getApiV1EventPoolId(poolId);
  return mapEventPool(response);
};

export const createCompetition = async (payload: TCompetition) => {
  await api.postApiV1EditorCompetition({
    name: payload.name,
    rang: payload.rang,
    countryName: payload.countryName,
  });
};

export const updateCompetition = async (payload: TCompetition) => {
  await api.patchApiV1EditorCompetitionId(payload.competitionId, {
    name: payload.name,
    rang: payload.rang,
    countryName: payload.countryName,
  });
};

export const deleteCompetition = async (payload: TCompetition) => {
  await api.deleteApiV1EditorCompetitionId(payload.competitionId);
};

export const createCountry = async (payload: TCountry) => {
  await api.postApiV1EditorCountry({ name: payload.name });
};

export const updateCountry = async (payload: TCountry) => {
  await api.patchApiV1EditorCountryId(payload.countryId, {
    name: payload.name,
  });
};

export const deleteCountry = async (payload: TCountry) => {
  await api.deleteApiV1EditorCountryId(payload.countryId);
};

export const createEventPool = async (payload: TEventPools) => {
  await api.postApiV1EditorPool({
    mode: toApiEventPoolMode(payload.mode as unknown as number),
    poolDate: payload.poolDate,
  });
};

export const updateEventPool = async (payload: TEventPools) => {
  await api.patchApiV1EditorPoolId(String(payload.id), {
    mode: toApiEventPoolMode(payload.mode as unknown as number),
    status: toApiEventPoolStatus((payload.status as unknown as number) ?? 101),
  });
};

export const deleteEventPool = async (payload: TEventPools) => {
  await api.deleteApiV1EditorPoolId(String(payload.id));
};

export const addEventRangeToPool = async (
  poolId: string,
  eventIdentifiers: string[],
) => {
  await api.postApiV1EditorPoolAddEvent({ poolId, eventIdentifiers });
};

export const deleteEventFromPool = async (poolId: string, eventId: string) => {
  await api.deleteApiV1EditorPoolRemoveEvent({ poolId, eventId });
};

export const createEvent = async (payload: TEvent) => {
  await api.postApiV1EditorEvent({
    startAtUtc: dateTimeToUtcIso(payload.startDate, payload.startTime),
    status: toApiEventStatus(payload.status as unknown as number),
    blueTeam: {
      teamId: payload.blueTeam.teamId ?? null,
      winProbability: Number(
        payload.blueTeamWinProbability ?? payload.blueTeam.winProbability,
      ),
    },
    redTeam: {
      teamId: payload.redTeam.teamId ?? null,
      winProbability: Number(
        payload.redTeamWinProbability ?? payload.redTeam.winProbability,
      ),
    },
    country: {
      name: payload.country?.name ?? '',
    },
    competition: {
      name: payload.competition?.name ?? '',
      rang: payload.competition?.rang ?? '',
      countryName:
        payload.country?.name ?? payload.competition?.countryName ?? '',
    },
  });
};

export const updateEvent = async (payload: TEvent) => {
  await api.patchApiV1EditorEventId(payload.id, {
    startAtUtc: dateTimeToUtcIso(payload.startDate, payload.startTime),
    status: toApiEventStatus(payload.status as unknown as number),
    blueTeam: {
      teamId: payload.blueTeam.teamId ?? null,
      resultScore: Number(
        payload.blueTeamResultScore ?? payload.blueTeam.resultScore ?? 0,
      ),
      winProbability: Number(
        payload.blueTeamWinProbability ?? payload.blueTeam.winProbability,
      ),
    },
    redTeam: {
      teamId: payload.redTeam.teamId ?? null,
      resultScore: Number(
        payload.redTeamResultScore ?? payload.redTeam.resultScore ?? 0,
      ),
      winProbability: Number(
        payload.redTeamWinProbability ?? payload.redTeam.winProbability,
      ),
    },
    country: {
      name: payload.country?.name ?? '',
    },
    competition: {
      name: payload.competition?.name ?? '',
      rang: payload.competition?.rang ?? '',
      countryName:
        payload.country?.name ?? payload.competition?.countryName ?? '',
    },
  });
};

export const deleteEvent = async (payload: TEvent) => {
  await api.deleteApiV1EditorEventId(payload.id);
};

export const createEventCollection = async (payload: TEvent[]) => {
  await api.postApiV1EditorEventCollection({
    events: payload.map((event) => ({
      startAtUtc: dateTimeToUtcIso(event.startDate, event.startTime),
      status: toApiEventStatus(event.status as unknown as number),
      blueTeam: {
        teamId: event.blueTeam.teamId ?? null,
        winProbability: Number(
          event.blueTeamWinProbability ?? event.blueTeam.winProbability,
        ),
      },
      redTeam: {
        teamId: event.redTeam.teamId ?? null,
        winProbability: Number(
          event.redTeamWinProbability ?? event.redTeam.winProbability,
        ),
      },
      country: {
        name: event.country?.name ?? '',
      },
      competition: {
        name: event.competition?.name ?? '',
        rang: event.competition?.rang ?? '',
        countryName:
          event.country?.name ?? event.competition?.countryName ?? '',
      },
    })),
  });
};

export const createTeam = async (payload: TTeam) => {
  await api.postApiV1EditorTeam({
    Emblem: payload.emblemFile ?? undefined,
    TeamName: payload.name,
  });
};

export const updateTeam = async (payload: TTeam) => {
  await api.patchApiV1EditorTeamId(payload.teamId, {
    Emblem: payload.emblemFile ?? undefined,
    TeamName: payload.name,
  });
};

export const deleteTeam = async (payload: TTeam) => {
  await api.deleteApiV1EditorTeamId(payload.teamId);
};

const mapProfileToUser = (profile: ProfileDetails): TUser => ({
  id: profile.id ?? '',
  singleStatisticId: 0,
  username: profile.username ?? '',
  gender: profile.gender ?? 'none',
  language: profile.language ?? 'english',
  isOnboardingCompleted: profile.isOnboardingCompleted ?? false,
  email: profile.email ?? '',
  role: 'USER',
  status: profile.isBanned ? UserStatus.Blocked : UserStatus.Active,
  lastActivityDate: new Date(),
});

const mapWalletBalances = (
  wallet: WalletDetails,
): NonNullable<TUser['walletBalances']> => ({
  rp: wallet.rpAmount,
  score: wallet.scoreAmount,
  patch: wallet.patchAmount,
  ticket: wallet.ticketAmount,
});

const mapProfileByIdToUser = (profile: ProfileDetailsById): TUser => ({
  id: profile.id ?? '',
  singleStatisticId: 0,
  username: profile.username ?? '',
  gender: profile.gender ?? 'none',
  language: profile.language ?? 'english',
  isOnboardingCompleted: profile.isOnboardingCompleted ?? false,
  email: profile.email ?? '',
  role: 'USER',
  status: profile.isBanned ? UserStatus.Blocked : UserStatus.Active,
  lastActivityDate: new Date(),
  walletBalances: mapWalletBalances(profile.wallet),
});

export const getUsers = async (pagination: Pagination) => {
  const response = await api.getApiV1UserCollection({
    Skip: pagination.skip,
    Take: pagination.take,
  });

  const data = (response?.profiles ?? []).map(mapProfileToUser);
  return {
    data,
    count: response?.count ?? data.length,
  };
};

export const getUserById = async (id: string | number): Promise<TUser> => {
  const response = await api.getApiV1UserId(String(id));

  if (!response) {
    throw new Error('Пользователь не найден');
  }

  return mapProfileByIdToUser(response);
};

export const adjustUserWalletBalance = async (
  userId: string | number,
  currency: WalletCurrency,
  amount: number,
): Promise<void> => {
  await api.patchApiV1EditorWalletUserIdAdjust(String(userId), {
    currency,
    amount,
    comment: 'Корректировка баланса администратором',
  });
};

export const updateUser = async (user: TUser): Promise<TUser> => {
  // There is no dedicated admin user update endpoint in the current OpenAPI.
  // Keep optimistic local update behaviour for now.
  return user;
};

export const addUser = async (user: TUser): Promise<TUser> => {
  // There is no admin create-user endpoint in the current OpenAPI.
  return user;
};

export const removeUser = async (user: TUser): Promise<boolean> => {
  // There is no admin delete-user endpoint in the current OpenAPI.
  void user;
  return false;
};

export const blockUser = async (user: TUser): Promise<boolean> => {
  await api.patchApiV1EditorUserIdBan(String(user.id), {
    banReason: 'Blocked from admin panel',
  });
  return true;
};

export const unblockUser = async (user: TUser): Promise<boolean> => {
  await api.patchApiV1EditorUserIdUnban(String(user.id), {});
  return true;
};

export const recoverUser = async (user: TUser): Promise<boolean> =>
  unblockUser(user);
